import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { saveAssistantReport } from '../api/assistant'
import Header from '../components/Header'
import Sidebar from '../components/Sidebar'
import AssistantCard from '../components/assistant/AssistantCard'
import ReportsModal from '../components/assistant/ReportsModal'
import { surface } from '../components/ui/surface'
import { useAssistantSession } from '../context/AssistantSessionContext'
import { useAssistantReports } from '../hooks/useAssistantReports'

const ghostButton =
  'inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-xl bg-white/[0.04] px-3 py-2 text-sm text-muted-200 transition hover:bg-white/[0.08] hover:text-white disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400'

const SUGGESTED_KEYS = [
  'assistant.suggestedQuestion',
  'assistant.suggestedRegions',
  'assistant.suggestedDevices',
  'assistant.suggestedTraffic',
  'assistant.suggestedSpeedtest',
  'assistant.suggestedProblems',
]

const Assistant = ({ onLogout, userEmail, userRole }) => {
  const { t } = useTranslation()
  const [isSidebarVisible, setSidebarVisible] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : true,
  )
  const [savingKey, setSavingKey] = useState(null)
  const [reportsOpen, setReportsOpen] = useState(false)
  const { messages, input, setInput, streaming, send, stop, reset, retry, markSaved } =
    useAssistantSession()
  const { reports, retentionDays, reload, remove } = useAssistantReports()
  const bottomRef = useRef(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streaming])

  const handleSave = async (index, cardIndex, card) => {
    const key = `${index}:${cardIndex}`
    setSavingKey(key)
    try {
      await saveAssistantReport({ payload: card })
      markSaved(index, cardIndex)
      await reload()
    } finally {
      setSavingKey(null)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    send(input)
  }

  const errorText = (code) =>
    (code === 'unconfigured' ? t('assistant.unconfigured') : code) || t('assistant.error')

  return (
    <div className="relative flex h-dvh flex-col overflow-hidden bg-surface-base text-muted-100">
      <Header
        isSidebarVisible={isSidebarVisible}
        toggleSidebar={() => setSidebarVisible((v) => !v)}
        userEmail={userEmail}
        userRole={userRole}
        onLogout={onLogout}
      />
      <div className="flex min-h-0 flex-1">
        <Sidebar
          onLogout={onLogout}
          userRole={userRole}
          isSidebarVisible={isSidebarVisible}
          onClose={() => setSidebarVisible(false)}
        />
        <main className="flex min-h-0 min-w-0 flex-1 flex-col bg-surface-base/60 px-4 py-6 lg:px-10">
          <div className="mx-auto flex min-h-0 w-full max-w-4xl flex-1 flex-col">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <h1 className="font-display text-2xl text-white">{t('assistant.title')}</h1>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setReportsOpen(true)}
                  className={ghostButton}
                >
                  <span className="material-icons text-lg">bookmarks</span>
                  {t('assistant.reportsButton', { count: reports.length })}
                </button>
                <button
                  type="button"
                  onClick={reset}
                  disabled={messages.length === 0}
                  className={ghostButton}
                >
                  <span className="material-icons text-lg">refresh</span>
                  {t('assistant.newDialog')}
                </button>
              </div>
            </div>

            <div className={`min-h-0 flex-1 space-y-4 overflow-y-auto rounded-3xl p-4 sm:p-6 ${surface.section}`}>
              {messages.length === 0 && (
                <div className="flex flex-col items-start gap-4 py-8">
                  <p className="text-sm text-muted-300">{t('assistant.empty')}</p>
                  <div className="flex flex-wrap gap-2">
                    {SUGGESTED_KEYS.map((key) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => send(t(key))}
                        className="min-h-11 cursor-pointer rounded-xl bg-white/[0.04] px-4 py-2 text-left text-sm text-muted-200 transition hover:bg-white/[0.08] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                      >
                        {t(key)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg, index) => (
                <article
                  key={`${msg.role}-${index}`}
                  className={msg.role === 'user' ? 'ml-8 sm:ml-16' : 'mr-4 sm:mr-12'}
                >
                  <p className="mb-1 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-500">
                    {msg.role === 'user' ? t('assistant.you') : t('assistant.title')}
                  </p>
                  <div
                    className={`rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-white/[0.08] text-white'
                        : `${surface.card} text-muted-100`
                    }`}
                  >
                    {msg.content || (streaming && index === messages.length - 1 ? t('assistant.thinking') : '')}
                    {(msg.cards?.length ? msg.cards : (msg.card ? [msg.card] : [])).map((card, cardIndex) => (
                      <AssistantCard
                        key={`${card.capturedAt || 'card'}-${cardIndex}`}
                        card={card}
                        saving={savingKey === `${index}:${cardIndex}`}
                        saved={Boolean(msg.savedCards?.[cardIndex] || (cardIndex === 0 && msg.saved))}
                        onSave={msg.role === 'assistant' ? (payload) => handleSave(index, cardIndex, payload) : undefined}
                      />
                    ))}
                    {msg.error && (
                      <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-white/[0.06] pt-3">
                        <p className="text-sm text-critical" role="alert">
                          {t('assistant.interrupted', { reason: errorText(msg.error) })}
                        </p>
                        <button
                          type="button"
                          onClick={retry}
                          disabled={streaming || index !== messages.length - 1}
                          className={ghostButton}
                        >
                          <span className="material-icons text-lg">replay</span>
                          {t('assistant.retry')}
                        </button>
                      </div>
                    )}
                  </div>
                </article>
              ))}
              <div ref={bottomRef} />
            </div>

            <form onSubmit={handleSubmit} className="mt-4 flex gap-2">
              <label htmlFor="assistant-input" className="sr-only">
                {t('assistant.placeholder')}
              </label>
              <input
                id="assistant-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t('assistant.placeholder')}
                className="min-h-11 flex-1 rounded-xl bg-white/[0.04] px-4 text-sm text-white placeholder:text-muted-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
              />
              {streaming ? (
                <button
                  type="button"
                  onClick={stop}
                  className="inline-flex h-11 min-w-11 cursor-pointer items-center justify-center rounded-xl bg-critical/10 px-4 text-critical transition hover:bg-critical/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                >
                  <span className="material-icons text-xl">stop</span>
                  <span className="sr-only">{t('assistant.stop')}</span>
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={!input.trim()}
                  className="inline-flex h-11 min-w-11 cursor-pointer items-center justify-center rounded-xl bg-white/[0.08] px-4 text-white transition hover:bg-white/[0.12] disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                >
                  <span className="material-icons text-xl">send</span>
                  <span className="sr-only">{t('assistant.send')}</span>
                </button>
              )}
            </form>
          </div>
        </main>
      </div>

      <ReportsModal
        isOpen={reportsOpen}
        onClose={() => setReportsOpen(false)}
        reports={reports}
        onDelete={remove}
        retentionDays={retentionDays}
      />
    </div>
  )
}

export default Assistant
